/**
 * Client-side mirror of backend/src/game/shared/character-roster.ts. This
 * copy additionally maps each class to concrete model/animation/equipment
 * filenames under frontend/public/assets/adventurers/ — no shared package
 * between backend/ and frontend/, so both copies are kept in sync by hand
 * (same convention as config/tuning.ts's NET/COMBAT/WEAPONS constants).
 * design/gdd/character-visuals.md Core Rule #1.
 */

export type CharacterClassId = 'rogue' | 'knight' | 'barbarian' | 'druid';
/** Mirror of the backend roster's role-based matchmaking amendment (2026-08-24). */
export type CharacterRole = 'TANK' | 'HEALER' | 'DPS';
/**
 * Mirrors `backend/src/game/dungeon-prototype/config/tuning.ts`.
 *
 * BLUNT and REACH (2026-09-04) are the second axis — what a swing does besides
 * damage — and both cost damage per second to buy it. See the backend table
 * for why BLUNT's impact is what it is.
 */
export type WeaponClass = 'LIGHT' | 'MEDIUM' | 'BLUNT' | 'HEAVY' | 'REACH';
export type RigSize = 'medium' | 'large';

export interface CharacterRosterEntry {
  id: CharacterClassId;
  weaponClass: WeaponClass;
  role: CharacterRole;
  /** Filename under characters/, without extension. */
  characterModel: string;
  rig: RigSize;
  /** Filename under equipment/, without extension. */
  weaponModel: string;
  /** Filename under equipment/, without extension — cosmetic only (Core Rule #8). */
  shieldModel?: string;
}

/**
 * Role-based matchmaking amendment (2026-08-24) — `druid` is the roster's
 * fourth class and the game's HEALER. It needs no new art and no new
 * animation work: `characters/druid.glb` ships the byte-identical 23-joint
 * skeleton as the other three (`root hips spine chest head upperarm.r
 * lowerarm.r wrist.r hand.r handslot.r ...`, verified against the GLB's own
 * skin), so it loads through the same `rig: 'medium'` animation sets and
 * the shared animation clips bind to it by bone name unchanged — those
 * bind by bone *name*, and every name they need is present.
 *
 * `druid_staff` was likewise already in `equipment/` and unused. It mounts
 * on the same `handslot.r` bone as every other weapon.
 */
export const CHARACTER_ROSTER: readonly CharacterRosterEntry[] = [
  { id: 'rogue', weaponClass: 'LIGHT', role: 'DPS', characterModel: 'rogue', rig: 'medium', weaponModel: 'dagger' },
  { id: 'knight', weaponClass: 'MEDIUM', role: 'TANK', characterModel: 'knight', rig: 'medium', weaponModel: 'sword_1handed', shieldModel: 'shield_round' },
  { id: 'barbarian', weaponClass: 'HEAVY', role: 'DPS', characterModel: 'barbarian', rig: 'medium', weaponModel: 'axe_2handed' },
  { id: 'druid', weaponClass: 'MEDIUM', role: 'HEALER', characterModel: 'druid', rig: 'medium', weaponModel: 'druid_staff' },
];

export function rosterEntry(id: string): CharacterRosterEntry {
  const found = CHARACTER_ROSTER.find((entry) => entry.id === id);
  // Edge Case #8 — an unrecognized class (stale data, a future roster entry this
  // build doesn't know about yet) falls back to the first roster entry rather
  // than throwing, so a render never hard-fails over a class-id mismatch.
  return found ?? CHARACTER_ROSTER[0];
}
